
import { useMemo, useState } from 'react';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';

import { getEmployeeName } from '../../../auth/utils/Login/authStorage';

/* ========================================================================= */
/* TYPES                                                                     */
/* ========================================================================= */

export interface MeVsTeamProductivityItem {
  day: string;
  me: number;
  team: number;
  target: number;
  teamName?: string;
  periodLabel?: string;
}

interface Props {
  data: MeVsTeamProductivityItem[];
  teamName?: string;
  periodLabel?: string;
}

type SeriesKey = 'me' | 'team';

interface HoveredBar {
  series: SeriesKey;
  point: MeVsTeamProductivityItem;
  value: number;
}

interface CustomTooltipProps {
  active?: boolean;
  hoveredBar: HoveredBar | null;
}

/* ========================================================================= */
/* COLORS                                                                    */
/* ========================================================================= */

const COLORS = {
  me: '#22C55E',
  team: '#94A3B8',
  target: '#EF4444',
};

/* ========================================================================= */
/* LEGEND TYPES                                                              */
/* ========================================================================= */

interface LegendProps {
  activeSeries: SeriesKey | null;
  setActiveSeries: React.Dispatch<
    React.SetStateAction<SeriesKey | null>
  >;
}

/* ========================================================================= */
/* CUSTOM LEGEND                                                             */
/* ========================================================================= */

const CustomLegend = ({
  activeSeries,
  setActiveSeries,
}: LegendProps) => {
  const employeeName = getEmployeeName() || 'Me';

  return (
    <div className="mb-4 flex w-full flex-wrap items-center justify-center gap-x-5 gap-y-2 sm:mb-5 sm:gap-x-6">
      <div
        className="flex min-w-0 cursor-pointer items-center gap-2"
        onMouseEnter={() => setActiveSeries('me')}
        onMouseLeave={() => setActiveSeries(null)}
      >
        <span
          className="h-[11px] w-[11px] shrink-0 rounded-[2px] sm:h-[13px] sm:w-[13px]"
          style={{
            backgroundColor: COLORS.me,
            opacity: activeSeries === 'team' ? 0.3 : 1,
          }}
        />

        <span
          className={`max-w-[180px] truncate text-[12px] sm:max-w-[240px] sm:text-[13px] ${
            activeSeries === 'me'
              ? 'font-semibold text-[#101828]'
              : 'font-medium text-[#344054]'
          }`}
          title={`Me (${employeeName})`}
        >
          Me ({employeeName})
        </span>
      </div>

      <div
        className="flex cursor-pointer items-center gap-2"
        onMouseEnter={() => setActiveSeries('team')}
        onMouseLeave={() => setActiveSeries(null)}
      >
        <span
          className="h-[11px] w-[11px] shrink-0 rounded-[2px] sm:h-[13px] sm:w-[13px]"
          style={{
            backgroundColor: COLORS.team,
            opacity: activeSeries === 'me' ? 0.3 : 1,
          }}
        />

        <span
          className={`whitespace-nowrap text-[12px] sm:text-[13px] ${
            activeSeries === 'team'
              ? 'font-semibold text-[#101828]'
              : 'font-medium text-[#344054]'
          }`}
        >
          Team Avg
        </span>
      </div>
    </div>
  );
};

/* ========================================================================= */
/* FORMAT                                                                    */
/* ========================================================================= */

const formatPercentage = (value: unknown): string => {
  const number = Number(value);

  return Number.isFinite(number)
    ? `${Number(number.toFixed(1))}%`
    : '0%';
};

/* ========================================================================= */
/* CUSTOM TOOLTIP                                                            */
/* ========================================================================= */

const CustomTooltip = ({
  active,
  hoveredBar,
}: CustomTooltipProps) => {
  if (!active || !hoveredBar) {
    return null;
  }

  const { series, point, value } = hoveredBar;

  const isProductivity = series === 'me';

  const seriesName = isProductivity
    ? 'Productivity'
    : 'Team Avg';

  const seriesColor = isProductivity
    ? COLORS.me
    : COLORS.team;

  const target = Number(point.target ?? 80);

  return (
    <div className="pointer-events-none z-[9999] min-w-[190px] max-w-[240px] overflow-hidden rounded-[8px] border border-[#D0D5DD] bg-white shadow-[0_8px_20px_rgba(16,24,40,0.14)]">
      {/* Exact date from the hovered bar */}
      <div className="border-b border-[#D0D5DD] bg-[#ECEFF3] px-3 py-2">
        <p className="truncate text-[11px] font-medium text-[#101828] sm:text-[12px]">
          {point.day}
        </p>
      </div>

      {/* Only the hovered series */}
      <div className="flex items-center justify-between gap-4 px-3 py-2.5 sm:py-3">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full sm:h-3 sm:w-3"
            style={{ backgroundColor: seriesColor }}
          />

          <span className="truncate text-[11px] text-[#101828] sm:text-[12px]">
            {seriesName}:
          </span>
        </div>

        <span className="shrink-0 text-[11px] font-semibold text-[#101828] sm:text-[12px]">
          {formatPercentage(value)}
        </span>
      </div>

      {/* Target remains visible */}
      <div className="flex items-center justify-between gap-4 border-t border-[#EAECF0] px-3 py-2">
        <div className="flex items-center gap-2">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full sm:h-3 sm:w-3"
            style={{ backgroundColor: COLORS.target }}
          />

          <span className="text-[11px] text-[#101828] sm:text-[12px]">
            Target:
          </span>
        </div>

        <span className="shrink-0 text-[11px] font-semibold text-[#101828] sm:text-[12px]">
          {formatPercentage(
            Number.isFinite(target) ? target : 80,
          )}
        </span>
      </div>
    </div>
  );
};

/* ========================================================================= */
/* COMPONENT                                                                 */
/* ========================================================================= */

const MeVsTeamProductivityChart = ({
  data,
  teamName,
  periodLabel,
}: Props) => {
  const [activeSeries, setActiveSeries] =
    useState<SeriesKey | null>(null);

  const [hoveredBar, setHoveredBar] =
    useState<HoveredBar | null>(null);

  /* EMPLOYEE NAME */

  const employeeName = useMemo(
    () => getEmployeeName() || 'Me',
    [],
  );

  /* DYNAMIC TEAM NAME */

  const dynamicTeamName = useMemo(() => {
    if (teamName?.trim()) {
      return teamName.trim();
    }

    const apiTeamName = data.find(
      (item) => item.teamName?.trim(),
    )?.teamName;

    if (apiTeamName?.trim()) {
      return apiTeamName.trim();
    }

    return employeeName !== 'Me'
      ? `${employeeName}'s Team`
      : 'My Team';
  }, [teamName, data, employeeName]);

  /* DYNAMIC PERIOD */

  const dynamicPeriodLabel = useMemo(() => {
    if (periodLabel?.trim()) {
      return periodLabel.trim();
    }

    const apiPeriod = data.find(
      (item) => item.periodLabel?.trim(),
    )?.periodLabel;

    return apiPeriod?.trim() || 'this week';
  }, [periodLabel, data]);

  /* NORMALIZE DATA */

  const chartData = useMemo(
    () =>
      data.map((item) => ({
        ...item,
        me: Number.isFinite(Number(item.me))
          ? Number(item.me)
          : 0,
        team: Number.isFinite(Number(item.team))
          ? Number(item.team)
          : 0,
        target:
          Number.isFinite(Number(item.target)) &&
          Number(item.target) > 0
            ? Number(item.target)
            : 80,
      })),
    [data],
  );

  /* TARGET */

  const target = chartData[0]?.target ?? 80;

  /* Y AXIS */

  const values = chartData.flatMap((item) => [
    item.me,
    item.team,
    item.target,
  ]);

  const minValue = values.length
    ? Math.min(...values)
    : 0;

  const maxValue = values.length
    ? Math.max(...values)
    : 100;

  let yMin = Math.max(
    0,
    Math.floor((minValue - 5) / 5) * 5,
  );

  let yMax = Math.min(
    100,
    Math.ceil((maxValue + 5) / 5) * 5,
  );

  if (yMax <= yMin) {
    yMin = Math.max(0, yMin - 10);
    yMax = Math.min(100, yMax + 10);
  }

  const ticks: number[] = [];

  for (let i = yMin; i <= yMax; i += 5) {
    ticks.push(i);
  }

  /* RESPONSIVE BAR SIZE */

  const barSize =
    chartData.length >= 7
      ? 16
      : chartData.length >= 5
        ? 20
        : 24;

  /*
   * Capture the original datum from the actual hovered bar.
   * This prevents the tooltip from reusing another date's
   * payload or label.
   */

  const handleBarHover = (
    series: SeriesKey,
    entry: any,
  ) => {
    const point = entry?.payload ?? entry;

    if (!point || typeof point.day !== 'string') {
      return;
    }

    const value = Number(
      series === 'me' ? point.me : point.team,
    );

    setActiveSeries(series);

    setHoveredBar({
      series,
      point,
      value: Number.isFinite(value) ? value : 0,
    });
  };

  const handleBarLeave = () => {
    setActiveSeries(null);
    setHoveredBar(null);
  };

  /* RENDER */

  return (
    <div className="w-full min-w-0 overflow-hidden rounded-[14px] border border-[#D0D5DD] bg-white p-3 sm:rounded-[16px] sm:p-4 lg:rounded-[18px] lg:p-5">
      {/* HEADER */}
      <div className="mb-3 flex min-w-0 flex-col items-start gap-2 sm:mb-4 sm:flex-row sm:items-center sm:gap-3">
        <h3
          className="max-w-full truncate text-[14px] font-semibold text-[#002B5B] sm:text-[15px] lg:text-[16px]"
          title="Me vs Team · Productivity"
        >
          Me vs Team · Productivity
        </h3>

        <div
          className="max-w-full min-w-0 rounded-full bg-[#EEF4FF] px-2.5 py-[4px] text-[10px] font-medium text-[#2563EB] sm:px-3 sm:py-[5px] sm:text-[11px] lg:text-[12px]"
          title={`Daily % · ${dynamicTeamName} · ${dynamicPeriodLabel}`}
        >
          <span className="block truncate">
            Daily % · {dynamicTeamName} · {dynamicPeriodLabel}
          </span>
        </div>
      </div>

      {/* LEGEND */}
      <CustomLegend
        activeSeries={activeSeries}
        setActiveSeries={setActiveSeries}
      />

      {/* CHART */}
      <div className="h-[200px] w-full min-w-0 sm:h-[220px] md:h-[230px] lg:h-[240px] xl:h-[250px]">
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <BarChart
            data={chartData}
            margin={{
              top: 8,
              right: 8,
              left: 0,
              bottom: 2,
            }}
            barCategoryGap={
              chartData.length >= 7 ? '18%' : '25%'
            }
            barGap={chartData.length >= 7 ? 1 : 2}
          >
            {/* GRID */}
            <CartesianGrid
              vertical={false}
              stroke="#EAECF0"
              strokeDasharray="2 2"
            />

            {/* X AXIS */}
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              tickMargin={8}
              interval={
                chartData.length > 7
                  ? 'preserveStartEnd'
                  : 0
              }
              tick={{
                fontSize: 10,
                fill: '#344054',
              }}
              minTickGap={8}
            />

            {/* Y AXIS */}
            <YAxis
              domain={[yMin, yMax]}
              ticks={ticks}
              width={42}
              axisLine={false}
              tickLine={false}
              tickMargin={5}
              tickFormatter={(value: number) => `${value}%`}
              tick={{
                fontSize: 10,
                fill: '#475467',
              }}
            />

            {/* Tooltip uses the hovered bar's captured data */}
            <Tooltip
              shared={false}
              trigger="hover"
              offset={10}
              cursor={{ fill: '#F8FAFC' }}
              wrapperStyle={{
                zIndex: 9999,
                outline: 'none',
              }}
              content={
                <CustomTooltip hoveredBar={hoveredBar} />
              }
            />

            {/* TARGET */}
            <ReferenceLine
              y={target}
              stroke={COLORS.target}
              strokeWidth={1.5}
              strokeDasharray="4 4"
            />

            {/* GREEN: PRODUCTIVITY */}
            <Bar
              dataKey="me"
              name={`Me (${employeeName})`}
              fill={COLORS.me}
              radius={[4, 4, 0, 0]}
              barSize={barSize}
              fillOpacity={
                activeSeries === 'team' ? 0.25 : 1
              }
              onMouseEnter={(entry: any) =>
                handleBarHover('me', entry)
              }
              onMouseLeave={handleBarLeave}
            />

            {/* GREY: TEAM AVG */}
            <Bar
              dataKey="team"
              name="Team Avg"
              fill={COLORS.team}
              radius={[4, 4, 0, 0]}
              barSize={barSize}
              fillOpacity={
                activeSeries === 'me' ? 0.25 : 1
              }
              onMouseEnter={(entry: any) =>
                handleBarHover('team', entry)
              }
              onMouseLeave={handleBarLeave}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MeVsTeamProductivityChart;
